import { MessageCircle, MessageSquare, Phone, UserRound } from 'lucide-react';

import { Badge } from '@/components/ui/Badge';
import { CHANNEL_LABEL, DELIVERY_STATUS_LABEL } from '@/constants/messaging';
import { formatDateTime } from '@/lib/format';
import type { ParentMessage } from '../../types/messages';

export interface MessageCardProps {
  message: ParentMessage;
}

/**
 * One message the school sent to this family's phone.
 *
 * The text as it went out, who sent it, and whether it reached the phone —
 * which is the question a parent is usually asking when they come looking for a
 * message they half-remember receiving. Tokens only, so both themes work.
 */
export function MessageCard({ message }: MessageCardProps) {
  const ChannelIcon = message.channel === 'WHATSAPP' ? MessageCircle : MessageSquare;
  const delivery = DELIVERY_STATUS_LABEL[message.status];

  return (
    <article className="card flex flex-col gap-3 p-4 sm:p-5" aria-labelledby={`message-${message.uuid}`}>
      <header className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <span
            className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-accent-soft text-accent"
            aria-hidden
          >
            <ChannelIcon className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <h2 id={`message-${message.uuid}`} className="text-base font-bold text-ink">
              {message.title}
            </h2>
            <p className="mt-0.5 text-sm text-ink-3">
              <time dateTime={message.sentAt}>{formatDateTime(message.sentAt)}</time>
              {' · '}
              {CHANNEL_LABEL[message.channel]}
            </p>
          </div>
        </div>

        <Badge tone={delivery.tone} className="self-start">
          {delivery.label}
        </Badge>
      </header>

      <p className="whitespace-pre-line break-words text-sm text-ink-2">{message.message}</p>

      <footer className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-line pt-3 text-sm text-ink-3">
        {message.sentBy && (
          <span className="inline-flex items-center gap-1.5">
            <UserRound className="h-4 w-4" aria-hidden />
            {message.sentBy.name}, {message.sentBy.role}
          </span>
        )}
        {message.sentTo && (
          <span className="inline-flex items-center gap-1.5">
            <Phone className="h-4 w-4" aria-hidden />
            Sent to {message.sentTo}&apos;s number
          </span>
        )}
        <span className="min-w-0 truncate">{message.audienceLabel}</span>
      </footer>
    </article>
  );
}
